import React from "react";
import { Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import Layout from "@/components/Layout";
import { useAuth } from "@/contexts/AuthContext";
import { roleHomePath } from "@/lib/constants";
import { Megaphone, Shield, LineChart, ArrowRight, ClipboardCheck, FileDown, BookOpen } from "lucide-react";

const HERO_IMG = "https://images.pexels.com/photos/11742414/pexels-photo-11742414.jpeg";
const NOISE_IMG = "https://images.unsplash.com/photo-1602951172321-fe0aa8865e6b";
const POLICE_IMG = "https://images.unsplash.com/photo-1773227059336-7f253a654fe6";
const CITIZEN_GUIDE_MR = "/nagpur-noise-guide-marathi.pdf";
const CITIZEN_GUIDE_EN = "/nagpur-noise-guide-english.pdf";
const EMPLOYEE_MANUAL_MR = "/nagpur-noise-manual-marathi.pdf";
const EMPLOYEE_MANUAL_EN = "/nagpur-noise-manual-english.pdf";

export default function Landing() {
  const { user } = useAuth();
  return (
    <Layout>
      {/* HERO */}
      <section className="relative overflow-hidden bg-slate-900 text-white">
        <div className="absolute inset-0">
          <img src={HERO_IMG} alt="Nagpur skyline" className="w-full h-full object-cover opacity-40" />
          <div className="absolute inset-0 bg-slate-900/70" />
        </div>
        <div className="relative max-w-7xl mx-auto px-6 lg:px-8 py-24 lg:py-32 grid lg:grid-cols-12 gap-10 items-center">
          <div className="lg:col-span-8">
            <div className="overline text-emerald-300 mb-4">Nagpur Police · Municipal Corporation</div>
            <h1 className="font-display font-bold text-4xl sm:text-5xl lg:text-6xl leading-[1.05] tracking-tight">
              Noise Free Nagpur.<br />
              <span className="text-emerald-300">Report. Track. Resolve — in hours, not weeks.</span>
            </h1>
            <p className="mt-6 text-lg text-slate-200 max-w-2xl leading-relaxed">
              A dedicated citizen grievance portal for Nagpur. File a noise complaint, track it in real time,
              and it is automatically routed to the Police Station Inspector with a strict 30-minute per-level SLA (PI → ACP → DCP).
            </p>
            <div className="mt-10 flex flex-wrap gap-3">
              <Link to={user ? roleHomePath(user.role) : "/register"}>
                <Button size="lg" data-testid="hero-file-complaint-btn" className="gap-2 h-12 px-6 text-base">
                  {user ? "Go to Dashboard" : "File a Complaint"} <ArrowRight className="w-4 h-4" />
                </Button>
              </Link>
              <Link to="/track">
                <Button size="lg" variant="outline" data-testid="hero-track-btn" className="gap-2 h-12 px-6 text-base bg-transparent text-white border-white/40 hover:bg-white hover:text-slate-900">
                  Track a Complaint
                </Button>
              </Link>
              <a href={CITIZEN_GUIDE_MR} target="_blank" rel="noreferrer" data-testid="hero-marathi-guide-btn">
                <Button size="lg" variant="outline" className="gap-2 h-12 px-6 text-base bg-transparent text-emerald-200 border-emerald-300/60 hover:bg-emerald-300 hover:text-slate-900">
                  <FileDown className="w-4 h-4" /> मराठी माहिती पत्रक (PDF)
                </Button>
              </a>
              <a href={CITIZEN_GUIDE_EN} target="_blank" rel="noreferrer" data-testid="hero-english-guide-btn">
                <Button size="lg" variant="outline" className="gap-2 h-12 px-6 text-base bg-transparent text-slate-100 border-white/40 hover:bg-white hover:text-slate-900">
                  <FileDown className="w-4 h-4" /> English Guide (PDF)
                </Button>
              </a>
            </div>
            <div className="mt-10 flex flex-wrap gap-8 text-sm text-slate-300">
              <div className="flex items-center gap-2"><Shield className="w-4 h-4 text-emerald-300" /> Zone-wise DCP routing</div>
              <div className="flex items-center gap-2"><ClipboardCheck className="w-4 h-4 text-emerald-300" /> 30-min SLA per level</div>
              <div className="flex items-center gap-2"><LineChart className="w-4 h-4 text-emerald-300" /> Auto-escalation to Addl CP</div>
            </div>
          </div>
        </div>
      </section>

      {/* HOW IT WORKS */}
      <section className="max-w-7xl mx-auto px-6 lg:px-8 py-20">
        <div className="max-w-3xl">
          <div className="overline text-slate-500 mb-3">How it works</div>
          <h2 className="font-display font-bold text-3xl sm:text-4xl tracking-tight">A simple, accountable process</h2>
          <p className="mt-3 text-slate-600 text-lg">From complaint filing to resolution — every step is tracked, timestamped, and open to review by the Commissioner, Collector, and Municipal Commissioner.</p>
        </div>
        <div className="mt-12 grid md:grid-cols-3 gap-6">
          {[
            {
              step: "01",
              icon: Megaphone,
              title: "File a complaint",
              text: "Add location, describe the source of noise, attach a photo or audio recording, and choose the zone.",
            },
            {
              step: "02",
              icon: Shield,
              title: "Auto-routed to zone DCP",
              text: "Your complaint is instantly assigned to the DCP responsible for your zone in Nagpur.",
            },
            {
              step: "03",
              icon: LineChart,
              title: "30-min SLA · Auto-escalation",
              text: "If not attended within 30 minutes, the complaint automatically escalates PI → ACP → DCP.",
            },
          ].map((it, i) => (
            <div key={i} className="p-6 rounded-lg border border-border bg-white hover:border-slate-400 transition-colors">
              <div className="flex items-start gap-3">
                <div className="overline text-slate-400">{it.step}</div>
                <it.icon className="w-5 h-5 text-slate-700 ml-auto" />
              </div>
              <h3 className="mt-6 font-display font-semibold text-xl tracking-tight">{it.title}</h3>
              <p className="mt-2 text-sm text-slate-600 leading-relaxed">{it.text}</p>
            </div>
          ))}
        </div>
      </section>

      {/* WHY IT MATTERS */}
      <section className="bg-slate-50 border-y border-border py-20">
        <div className="max-w-7xl mx-auto px-6 lg:px-8 grid lg:grid-cols-2 gap-12 items-center">
          <div>
            <div className="overline text-slate-500 mb-3">Why it matters</div>
            <h2 className="font-display font-bold text-3xl sm:text-4xl tracking-tight">Noise pollution is a serious public health issue.</h2>
            <p className="mt-4 text-slate-600 leading-relaxed">
              Chronic exposure to noise above permissible limits contributes to cardiovascular disease, sleep disruption,
              and hearing loss. This portal ensures every citizen has a fast, transparent way to escalate noise violations
              directly to the enforcement authority.
            </p>
            <ul className="mt-6 space-y-2 text-slate-700 text-sm">
              <li>· CPCB permissible limits: 55 dB(A) day / 45 dB(A) night — residential.</li>
              <li>· Loudspeakers restricted after 10 PM in most zones.</li>
              <li>· Violations are punishable under the Noise Pollution (Regulation &amp; Control) Rules, 2000.</li>
            </ul>
          </div>
          <div className="grid grid-cols-2 gap-4">
            <img src={NOISE_IMG} alt="Traffic noise" className="rounded-lg object-cover aspect-[4/5] w-full" />
            <img src={POLICE_IMG} alt="Police interacting with citizens" className="rounded-lg object-cover aspect-[4/5] w-full mt-8" />
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="max-w-7xl mx-auto px-6 lg:px-8 py-20 text-center">
        <h2 className="font-display font-bold text-3xl sm:text-4xl tracking-tight">Ready to make Nagpur quieter?</h2>
        <p className="mt-3 text-slate-600 max-w-2xl mx-auto">Register once. File as many complaints as you need. Track every step until resolution.</p>
        <div className="mt-8 flex justify-center gap-3 flex-wrap">
          <Link to={user ? roleHomePath(user.role) : "/register"}>
            <Button size="lg" data-testid="cta-register-btn" className="gap-2 h-12 px-6">
              {user ? "Continue" : "Register as a Citizen"} <ArrowRight className="w-4 h-4" />
            </Button>
          </Link>
          <Link to="/login">
            <Button size="lg" variant="outline" data-testid="cta-login-btn" className="h-12 px-6">
              Officer / Admin Login
            </Button>
          </Link>
        </div>
      </section>

      <footer className="border-t border-border bg-white py-10">
        <div className="max-w-7xl mx-auto px-6 lg:px-8 grid md:grid-cols-2 gap-8 items-start">
          <div>
            <div className="overline text-slate-500 mb-2">Downloads</div>
            <div className="font-display font-semibold text-lg mb-3">Guides &amp; Manuals</div>
            <div className="flex flex-wrap gap-2">
              <a href={CITIZEN_GUIDE_MR} target="_blank" rel="noreferrer" data-testid="footer-citizen-mr-btn">
                <Button variant="outline" size="sm" className="gap-2"><FileDown className="w-4 h-4" /> नागरिक माहिती पत्रक (मराठी)</Button>
              </a>
              <a href={CITIZEN_GUIDE_EN} target="_blank" rel="noreferrer" data-testid="footer-citizen-en-btn">
                <Button variant="outline" size="sm" className="gap-2"><FileDown className="w-4 h-4" /> Citizen Guide (English)</Button>
              </a>
              <a href={EMPLOYEE_MANUAL_MR} target="_blank" rel="noreferrer" data-testid="footer-manual-mr-btn">
                <Button variant="outline" size="sm" className="gap-2"><BookOpen className="w-4 h-4" /> कर्मचारी हस्तपुस्तिका (मराठी)</Button>
              </a>
              <a href={EMPLOYEE_MANUAL_EN} target="_blank" rel="noreferrer" data-testid="footer-manual-en-btn">
                <Button variant="outline" size="sm" className="gap-2"><BookOpen className="w-4 h-4" /> Employee Manual (English)</Button>
              </a>
            </div>
          </div>
          <div className="text-sm text-slate-500 md:text-right">
            © {new Date().getFullYear()} Noise Free Nagpur · Nagpur Police &amp; Nagpur Municipal Corporation
          </div>
        </div>
      </footer>
    </Layout>
  );
}
